import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './ArticleshipList.css';
import { apiCall } from '../utils/api';
import JobApplicationModal from './JobApplicationModal';
import trackEvent from '../utils/analytics';

const CustomDropdown = ({ label, value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('touchstart', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('touchstart', handleClick);
        };
    }, []);

    const filtered = options.filter(opt =>
        opt.label.toLowerCase().includes(search.toLowerCase())
    );

    const selectedLabel = value
        ? (options.find(o => o.value === value)?.label || value)
        : placeholder;

    return (
        <div className="al-custom-dropdown" ref={ref}>
            <label>{label}</label>
            <div
                className={`al-dropdown-trigger ${open ? 'al-dropdown-open' : ''} ${value ? 'al-dropdown-has-value' : ''}`}
                onClick={() => setOpen(!open)}
            >
                <span className="al-dropdown-text">{selectedLabel}</span>
                <span className="al-dropdown-arrow">▾</span>
            </div>
            {open && (
                <div className="al-dropdown-menu">
                    {options.length > 5 && (
                        <div className="al-dropdown-search-wrap">
                            <input
                                type="text"
                                className="al-dropdown-search"
                                placeholder="Search..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                            />
                        </div>
                    )}
                    <div className="al-dropdown-options">
                        <div
                            className={`al-dropdown-option ${!value ? 'al-dropdown-option-active' : ''}`}
                            onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
                        >
                            {placeholder}
                        </div>
                        {filtered.map(opt => (
                            <div
                                key={opt.value}
                                className={`al-dropdown-option ${value === opt.value ? 'al-dropdown-option-active' : ''}`}
                                onClick={() => { onChange(opt.value); setOpen(false); setSearch(''); }}
                            >
                                {opt.label}
                            </div>
                        ))}
                        {filtered.length === 0 && (
                            <div className="al-dropdown-empty">No results</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const HARDCODED_ARTICLESHIP_COUNT = 52;

const ArticleshipList = ({ onTriggerOnboarding, pendingApplication, onClearPendingApplication }) => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [allJobsLoaded, setAllJobsLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [totalJobCount, setTotalJobCount] = useState(HARDCODED_ARTICLESHIP_COUNT);
    const [selectedJob, setSelectedJob] = useState(null);
    const [modalMode, setModalMode] = useState('continue'); // 'continue' or 'submit'
    const [savedFormData, setSavedFormData] = useState(null);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    const [activeTab, setActiveTab] = useState('new');
    const isNewTabActive = activeTab === 'new';
    const [appliedJobs, setAppliedJobs] = useState([]);
    const [applicationsLoading, setApplicationsLoading] = useState(true);
    const [applicationsError, setApplicationsError] = useState('');

    // Filter states
    const [showFilters, setShowFilters] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [filterFirmSize, setFilterFirmSize] = useState('');
    const [filterFirmName, setFilterFirmName] = useState('');
    const [filterPartner, setFilterPartner] = useState('');
    const [filterArea, setFilterArea] = useState('');
    const [sortBy, setSortBy] = useState('newest');

    // Firm details modal
    const [viewingFirm, setViewingFirm] = useState(null);
    const [loadingFirmId, setLoadingFirmId] = useState(null);
    const [firmLogos, setFirmLogos] = useState({});
    const [photoViewerUrl, setPhotoViewerUrl] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const PAGE = 5;

        const loadAllJobs = async () => {
            try {
                setLoading(true);
                setError(null);

                let allJobs = [];
                let skip = 0;

                while (true) {
                    if (skip > 0) {
                        setLoadingMore(true);
                    }
                    
                    const response = await apiCall.get(`/jobs?limit=${PAGE}&skip=${skip}`);
                    if (cancelled) return;

                    const jobsData = response.data || response || [];
                    const batch = Array.isArray(jobsData) ? jobsData : [];
                    allJobs = [...allJobs, ...batch];

                    if (skip === 0) {
                        setJobs(allJobs);
                        setLoading(false);
                    } else {
                        setJobs(allJobs);
                    }

                    if (batch.length < PAGE) {
                        setAllJobsLoaded(true);
                        break;
                    }
                    skip += PAGE;
                }

                if (!cancelled) {
                    setJobs(allJobs);
                    setLoadingMore(false);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching jobs:', err);
                    setError('Failed to load jobs');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                    setLoadingMore(false);
                }
            }
        };
        loadAllJobs();
        return () => { cancelled = true; };
    }, []);

    const getJobId = (job) => {
        if (!job) return null;
        return job._id || job.id || job.job_id || job.jobId || null;
    };

    const getJobIdFromApplication = (application) => {
        if (!application) return null;
        // Try to get job_id directly from application
        if (application.job_id) return application.job_id;
        if (application.jobId) return application.jobId;
        // Try to get from nested job object
        if (application.job) {
            return application.job._id || application.job.id || application.job.job_id;
        }
        if (application.job_details) {
            return application.job_details._id || application.job_details.id || application.job_details.job_id;
        }
        // Fallback to application's own _id/id
        return application._id || application.id || null;
    };

    const getJobFromApplication = (application) => {
        if (!application) return null;
        return application.job || application.job_details || application;
    };

    // Fetch logos for all unique firm_ids in the job list
    useEffect(() => {
        if (jobs.length === 0) return;
        const uniqueFirmIds = [...new Set(jobs.map(j => j.firm_id).filter(Boolean))];
        const missing = uniqueFirmIds.filter(id => !(id in firmLogos));
        if (missing.length === 0) return;

        missing.forEach(async (firmId) => {
            try {
                const response = await apiCall.get(`/firm/${firmId}`);
                const data = response.data || response;
                if (data.logo_url) {
                    setFirmLogos(prev => ({ ...prev, [firmId]: data.logo_url }));
                } else {
                    setFirmLogos(prev => ({ ...prev, [firmId]: null }));
                }
            } catch {
                setFirmLogos(prev => ({ ...prev, [firmId]: null }));
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobs]);

    const fetchFirmDetails = async (firmId, job) => {
        // Track view firm clicked event
        trackEvent('view_firm_clicked', {
            firm_id: firmId,
            firm_name: job?.firm_name,
            job_id: job?._id || job?.id
        });

        const loadingKey = firmId || job._id;
        if (!firmId) {
            setViewingFirm(job);
            return;
        }
        
        setLoadingFirmId(loadingKey);
        try {
            const response = await apiCall.get(`/firm/${firmId}`);
            const firmData = response.data || response;
            setViewingFirm({ ...job, ...firmData });
            if (firmData.logo_url) {
                setFirmLogos(prev => ({ ...prev, [firmId]: firmData.logo_url }));
            }
        } catch (err) {
            console.error('Error fetching firm details:', err);
            setViewingFirm(job);
        } finally {
            setLoadingFirmId(null);
        }
    };

    // When returning from onboarding with a pending application, re-open modal in submit mode
    useEffect(() => {
        if (pendingApplication) {
            setSelectedJob(pendingApplication.job);
            setSavedFormData(pendingApplication.formData);
            setModalMode('submit');
        }
    }, [pendingApplication]);

    const fetchUserApplications = async () => {
        setApplicationsLoading(true);
        setApplicationsError('');
        try {
            const response = await apiCall.get('/jobs/applications/user');
            const data = response.data || response || [];
            setAppliedJobs(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching user applications:', err);
            setApplicationsError('Failed to load your applications');
        } finally {
            setApplicationsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserApplications();
    }, []);

    const [quickApplyData, setQuickApplyData] = useState(null);
    const [applyCheckingJobId, setApplyCheckingJobId] = useState(null);

    const extractResumeUrl = (payload) => {
        if (!payload || typeof payload !== 'object') return null;
        return (
            payload.url ||
            payload.public_url ||
            payload?.data?.url ||
            payload?.data?.public_url ||
            null
        );
    };

    const handleApply = async (job) => {
        trackEvent('articleship_apply', {
            job_id: job?._id || job?.id,
            firm_id: job?.firm_id,
            firm_name: job?.firm_name
        });

        const jobId = getJobId(job);
        if (job.firm_id && jobId) {
            setApplyCheckingJobId(jobId);
        }

        let userPayload = {};
        try {
            const userRes = await apiCall.get('/users/get/user/details');
            userPayload = userRes?.data?.data || userRes?.data || userRes || {};
        } catch (err) {
            console.error('Failed to fetch user details for quick apply:', err);
        }

        const userId =
            userPayload?.user_id ||
            userPayload?._id ||
            userPayload?.id ||
            localStorage.getItem('user_id');

        try {
            if (job.firm_id && userId) {
                const resumeRes = await apiCall.get(`/jobs/firms/${job.firm_id}/users/${userId}/resume`);
                const resumePayload = resumeRes?.data || resumeRes || {};

                const resumePublicUrl = extractResumeUrl(resumePayload);
                if (resumePayload.has_resume && resumePublicUrl) {
                    const phone = resumePayload.number || userPayload.phone || userPayload.number || '';
                    const email = resumePayload.email || userPayload.email || '';
                    setQuickApplyData({
                        number: phone ? String(phone).replace(/\D/g, '') : '',
                        email: email || '',
                        resumeUrl: resumePublicUrl,
                    });
                    setSelectedJob(job);
                    setModalMode('quick');
                    setSavedFormData(null);
                    return;
                }
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status && status !== 404) {
                console.error('Quick-apply resume lookup failed:', err);
            }
        } finally {
            if (job.firm_id && jobId) {
                setApplyCheckingJobId(null);
            }
        }

        setQuickApplyData(null);
        setSelectedJob(job);
        setModalMode('continue');
        setSavedFormData(null);
    };

    const handleCloseModal = () => {
        setSelectedJob(null);
        setModalMode('continue');
        setSavedFormData(null);
        setQuickApplyData(null);
        if (pendingApplication) {
            onClearPendingApplication && onClearPendingApplication();
        }
    };

    const handleContinue = (formData, job) => {
        setSelectedJob(null);
        if (onTriggerOnboarding) {
            onTriggerOnboarding(formData, job);
        }
    };

    const handleSubmitSuccess = () => {
        setSelectedJob(null);
        setModalMode('continue');
        setSavedFormData(null);
        setQuickApplyData(null);
        if (pendingApplication) {
            onClearPendingApplication && onClearPendingApplication();
        }
        void fetchUserApplications();
    };

    // Extract unique filter options from jobs data
    const filterOptions = useMemo(() => {
        const cities = new Set();
        const firmSizes = new Set();
        const firmNames = new Set();
        const partners = new Set();
        const areas = new Set();

        jobs.forEach(job => {
            if (job.city) cities.add(job.city);
            if (job.firm_strength) firmSizes.add(job.firm_strength);
            if (job.firm_name) firmNames.add(job.firm_name);
            if (job.partner_name) partners.add(job.partner_name);
            (job.areas_of_articleship || []).forEach(area => areas.add(area));
        });

        return {
            cities: Array.from(cities).sort(),
            firmSizes: Array.from(firmSizes).sort((a, b) => a - b),
            firmNames: Array.from(firmNames).sort(),
            partners: Array.from(partners).sort(),
            areas: Array.from(areas).sort()
        };
    }, [jobs]);

    const activeFilterCount = [filterCity, filterFirmSize, filterFirmName, filterPartner, filterArea].filter(Boolean).length;

    const clearAllFilters = () => {
        setSearchQuery('');
        setFilterCity('');
        setFilterFirmSize('');
        setFilterFirmName('');
        setFilterPartner('');
        setFilterArea('');
    };

    const filteredJobs = jobs.filter(job => {
        // Search by firm name or partner name
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const firmMatch = (job.firm_name || '').toLowerCase().includes(query);
            const partnerMatch = (job.partner_name || '').toLowerCase().includes(query);
            if (!firmMatch && !partnerMatch) return false;
        }
        
        if (filterCity && job.city !== filterCity) return false;
        if (filterFirmSize && job.firm_strength !== parseInt(filterFirmSize)) return false;
        if (filterFirmName && job.firm_name !== filterFirmName) return false;
        if (filterPartner && job.partner_name !== filterPartner) return false;
        if (filterArea && !(job.areas_of_articleship || []).includes(filterArea)) return false;
        
        return true;
    });

    const appliedJobIds = useMemo(() => {
        const ids = new Set();
        (appliedJobs || []).forEach(application => {
            const id = getJobIdFromApplication(application);
            if (id) {
                ids.add(String(id));
            }
        });
        console.log('Applied job IDs:', [...ids]);
        console.log('Raw appliedJobs:', appliedJobs);
        return ids;
    }, [appliedJobs]);

    const sortedJobs = useMemo(() => {
        const base = [...filteredJobs];
        if (sortBy === 'newest') {
            base.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
        return base;
    }, [filteredJobs, sortBy]);

    const newJobs = useMemo(() => {
        return sortedJobs.filter(job => {
            const jobId = getJobId(job);
            return jobId ? !appliedJobIds.has(String(jobId)) : true;
        });
    }, [sortedJobs, appliedJobIds]);

    const appliedJobsSorted = useMemo(() => {
        // Log all job IDs in the main list for debugging
        console.log('All job IDs in main list:', sortedJobs.map(j => ({ _id: j._id, id: j.id, job_id: j.job_id })));
        
        // Get full job details from the main jobs list for applied jobs
        const appliedFromMainList = sortedJobs.filter(job => {
            const jobId = getJobId(job);
            const isApplied = jobId ? appliedJobIds.has(String(jobId)) : false;
            if (isApplied) {
                console.log('Found applied job in main list:', jobId);
            }
            return isApplied;
        });
        console.log('Applied jobs from main list:', appliedFromMainList.length);
        return appliedFromMainList;
    }, [sortedJobs, appliedJobIds]);

    const jobListToDisplay = activeTab === 'new' ? newJobs : appliedJobsSorted;
    const isAppliedTabActive = activeTab === 'applied';
    const jobListEmptyMessage = isAppliedTabActive
        ? 'You have not applied to any openings yet'
        : 'No articleship openings available';

    if (loading) {
        return (
            <div className="al-container">
                <div className="al-loading">Loading jobs...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="al-container">
                <div className="al-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="al-container">
            <div className="al-header">
                <h1>Articleship Openings</h1>
            </div>

            <div className="al-tab-row">
                <button
                    className={`al-tab-button ${activeTab === 'new' ? 'active' : ''}`}
                    onClick={() => setActiveTab('new')}
                >
                    New
                    <span className="al-tab-badge">{Math.max(0, HARDCODED_ARTICLESHIP_COUNT - appliedJobIds.size)}</span>
                </button>
                <button
                    className={`al-tab-button ${isAppliedTabActive ? 'active' : ''}`}
                    onClick={() => setActiveTab('applied')}
                >
                    Applied
                    <span className="al-tab-badge">{appliedJobIds.size}</span>
                    {applicationsLoading && <span className="al-tab-loading">Updating…</span>}
                </button>
            </div>

            {/* Search and Filter Bar */}
            <div className="al-search-bar">
                <div className="al-search-input-wrap">
                    <span className="al-search-icon">🔍</span>
                    <input
                        type="text"
                        className="al-search-input"
                        placeholder="Search by firm or partner name..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <button 
                    className={`al-filter-btn ${activeFilterCount > 0 ? 'al-filter-btn-active' : ''}`}
                    onClick={() => setShowFilters(true)}
                >
                    <svg className="al-filter-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="6" x2="14" y2="6"/><line x1="4" y1="12" x2="10" y2="12"/><line x1="4" y1="18" x2="14" y2="18"/><circle cx="17" cy="6" r="3"/><circle cx="13" cy="12" r="3"/><circle cx="17" cy="18" r="3"/></svg>
                    {activeFilterCount > 0 && (
                        <span className="al-filter-badge">{activeFilterCount}</span>
                    )}
                </button>
            </div>

            {/* Filter Page - full screen overlay */}
            {showFilters && ReactDOM.createPortal(
                <div className="al-filter-page">
                    <div className="al-filter-modal-card">
                        <div className="al-filter-page-header">
                            <button className="al-filter-back" onClick={() => setShowFilters(false)}>←</button>
                            <h2>Filters</h2>
                            <button className="al-filter-clear-link" onClick={clearAllFilters}>Clear</button>
                        </div>
                        
                        <div className="al-filter-page-body">
                        {filterOptions.firmNames.length > 0 && (
                            <CustomDropdown
                                label="Firm Name"
                                value={filterFirmName}
                                onChange={setFilterFirmName}
                                options={filterOptions.firmNames.map(n => ({ value: n, label: n }))}
                                placeholder="All Firms"
                            />
                        )}

                        {filterOptions.partners.length > 0 && (
                            <CustomDropdown
                                label="Partner Name"
                                value={filterPartner}
                                onChange={setFilterPartner}
                                options={filterOptions.partners.map(n => ({ value: n, label: n }))}
                                placeholder="All Partners"
                            />
                        )}

                        {filterOptions.cities.length > 0 && (
                            <CustomDropdown
                                label="City"
                                value={filterCity}
                                onChange={setFilterCity}
                                options={filterOptions.cities.map(c => ({ value: c, label: c }))}
                                placeholder="All Cities"
                            />
                        )}

                        {filterOptions.firmSizes.length > 0 && (
                            <CustomDropdown
                                label="Firm Size"
                                value={filterFirmSize}
                                onChange={setFilterFirmSize}
                                options={filterOptions.firmSizes.map(s => ({ value: String(s), label: `${s} employees` }))}
                                placeholder="All Sizes"
                            />
                        )}

                        {filterOptions.areas.length > 0 && (
                            <CustomDropdown
                                label="Area of Articleship"
                                value={filterArea}
                                onChange={setFilterArea}
                                options={filterOptions.areas.map(a => ({ value: a, label: a }))}
                                placeholder="All Areas"
                            />
                        )}
                    </div>

                        <div className="al-filter-page-footer">
                            <button className="al-filter-apply" onClick={() => setShowFilters(false)}>
                                Apply Filters {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Job list */}
            <div className="al-jobs">
                {isAppliedTabActive && applicationsLoading ? (
                    <div className="al-loading">Loading your applications...</div>
                ) : jobListToDisplay.length === 0 ? (
                    <div className="al-empty">
                        <p>{jobListEmptyMessage}</p>
                        {isAppliedTabActive && applicationsError && (
                            <p className="al-error al-application-error">{applicationsError}</p>
                        )}
                    </div>
                ) : (
                    <>
                        {isAppliedTabActive && applicationsError && (
                            <p className="al-error al-application-error">{applicationsError}</p>
                        )}
                        {jobListToDisplay.map((job, idx) => {
                            const jobId = getJobId(job);
                            const jobKey = jobId || job._id || job.id || job.job_id || job.jobId || idx;
                            const descKey = job._id || jobKey;
                            const isExpanded = expandedDescriptions[descKey];
                            const isAppliedJob = isAppliedTabActive || appliedJobIds.has(String(getJobId(job)));
                            return (
                                <div key={jobKey} className="al-job-card">
                                    <div className="al-job-header">
                                        <div className="al-job-firm">
                                            {(job.logo_url || firmLogos[job.firm_id]) ? (
                                                <img 
                                                    src={job.logo_url || firmLogos[job.firm_id]} 
                                                    alt={job.firm_name || 'Firm'} 
                                                    className="al-firm-avatar-img"
                                                />
                                            ) : (
                                                <div className="al-firm-avatar">
                                                    {(job.firm_name || 'F').charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <div className="al-firm-info">
                                                <h3 className="al-firm-name">{job.firm_name || 'CA Firm'}</h3>
                                                {(job.city || job.partner_name) && (
                                                    <p className="al-firm-address">
                                                        {job.city}{job.city && job.partner_name ? ' • ' : ''}{job.partner_name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                        {job.about_job && (
                                        <div className="al-job-description-wrap">
                                                <p className={`al-job-description ${isExpanded ? 'al-desc-expanded' : 'al-desc-collapsed'}`}>
                                                {job.about_job}
                                            </p>
                                            {job.about_job.length > 150 && (
                                                <button
                                                    className="al-desc-toggle"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                            setExpandedDescriptions(prev => ({
                                                                ...prev,
                                                                [descKey]: !prev[descKey]
                                                        }));
                                                    }}
                                                >
                                                        {isExpanded ? 'Show less' : 'More'}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="al-job-meta">
                                        <span className="al-positions">{job.number_of_positions} positions</span>
                                        {job.resume_required && (
                                            <span className="al-resume-badge">Resume Required</span>
                                        )}
                                    </div>

                                    <div className="al-job-areas">
                                        {(job.areas_of_articleship || []).slice(0, 4).map((area, i) => (
                                            <span key={i} className="al-area-tag">{area}</span>
                                        ))}
                                        {(job.areas_of_articleship || []).length > 4 && (
                                            <span className="al-area-more">+{job.areas_of_articleship.length - 4}</span>
                                        )}
                                    </div>

                                    <div className={`al-job-actions ${isAppliedTabActive ? 'al-job-actions-applied' : ''}`}>
                                        <button 
                                            className={`al-view-firm-btn ${isAppliedTabActive ? 'al-view-firm-btn-full' : ''}`}
                                            onClick={() => fetchFirmDetails(job.firm_id, job)}
                                            disabled={loadingFirmId === (job.firm_id || job._id)}
                                        >
                                            {loadingFirmId === (job.firm_id || job._id) ? 'Loading...' : 'View Firm'}
                                        </button>
                                        {!isAppliedTabActive && (
                                            <button 
                                                className="al-apply-btn"
                                                onClick={() => handleApply(job)}
                                                disabled={Boolean(jobId) && applyCheckingJobId === jobId}
                                            >
                                                {Boolean(jobId) && applyCheckingJobId === jobId ? 'Checking...' : 'Apply Now'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {loadingMore && !allJobsLoaded && (
                            <div className="al-loading-more">
                                <div className="al-loading-spinner"></div>
                                <span>Loading more articleships...</span>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Firm Details Modal */}
            {viewingFirm && ReactDOM.createPortal(
                <div className="al-firm-modal-overlay" onClick={() => setViewingFirm(null)}>
                    <div className="al-firm-modal" onClick={(e) => e.stopPropagation()}>
                        <button className="al-firm-modal-close" onClick={() => setViewingFirm(null)}>×</button>
                        
                        <div className="al-firm-modal-header">
                            {viewingFirm.logo_url ? (
                                <img 
                                    src={viewingFirm.logo_url} 
                                    alt={viewingFirm.firm_name || 'Firm'} 
                                    className="al-firm-modal-logo"
                                />
                            ) : (
                                <div className="al-firm-modal-avatar">
                                    {(viewingFirm.firm_name || 'F').charAt(0).toUpperCase()}
                                </div>
                            )}
                            <h2 className="al-firm-modal-name">{viewingFirm.firm_name || 'CA Firm'}</h2>
                        </div>

                        <div className="al-firm-modal-body">
                            {viewingFirm.address && (
                                <div className="al-firm-modal-section">
                                    <h4>Address</h4>
                                    <p>{viewingFirm.address}</p>
                                </div>
                            )}

                            {viewingFirm.city && (
                                <div className="al-firm-modal-section">
                                    <h4>City</h4>
                                    <p>{viewingFirm.city}</p>
                                </div>
                            )}

                            {viewingFirm.firm_strength && (
                                <div className="al-firm-modal-section">
                                    <h4>Firm Strength (Team Size)</h4>
                                    <p>{viewingFirm.firm_strength} members</p>
                                </div>
                            )}

                            {(viewingFirm.partner_names?.length > 0 || viewingFirm.partners?.length > 0 || viewingFirm.partner_name) && (
                                <div className="al-firm-modal-section">
                                    <h4>Partners</h4>
                                    <div className="al-firm-modal-partners">
                                        {(() => {
                                            const allPartners = new Set();
                                            if (Array.isArray(viewingFirm.partners)) {
                                                viewingFirm.partners.forEach(p => p && allPartners.add(p));
                                            }
                                            if (Array.isArray(viewingFirm.partner_names)) {
                                                viewingFirm.partner_names.forEach(p => p && allPartners.add(p));
                                            }
                                            if (viewingFirm.partner_name) {
                                                allPartners.add(viewingFirm.partner_name);
                                            }
                                            return [...allPartners].map((p, i) => (
                                                <span key={i} className="al-firm-modal-partner-chip">{p}</span>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            )}

                            {viewingFirm.firm_profile && (
                                <div className="al-firm-modal-section">
                                    <h4>Areas of Expertise</h4>
                                    {Array.isArray(viewingFirm.firm_profile) ? (
                                        <div className="al-firm-modal-tags">
                                            {viewingFirm.firm_profile.map((area, i) => (
                                                <span key={i} className="al-firm-modal-tag">{area}</span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="al-firm-modal-profile-text">{viewingFirm.firm_profile}</p>
                                    )}
                                </div>
                            )}

                            {viewingFirm.photos?.length > 0 && (
                                <div className="al-firm-modal-section">
                                    <h4>Firm Photos</h4>
                                    <div className="al-firm-modal-photos">
                                        {viewingFirm.photos.map((photo, i) => (
                                            <img 
                                                key={i} 
                                                src={photo} 
                                                alt={`Firm photo ${i + 1}`} 
                                                className="al-firm-modal-photo"
                                                onClick={() => setPhotoViewerUrl(photo)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="al-firm-modal-footer">
                            <button 
                                className="al-firm-modal-apply-btn"
                                onClick={() => {
                                    setViewingFirm(null);
                                    handleApply(viewingFirm);
                                }}
                            >
                                Apply Now
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {photoViewerUrl && ReactDOM.createPortal(
                <div className="al-photo-viewer-overlay" onClick={() => setPhotoViewerUrl(null)}>
                    <div className="al-photo-viewer-content" onClick={(e) => e.stopPropagation()}>
                        <button className="al-photo-viewer-close" onClick={() => setPhotoViewerUrl(null)}>×</button>
                        <img src={photoViewerUrl} alt="Firm photo" />
                    </div>
                </div>,
                document.body
            )}

            {/* Application Modal */}
            {selectedJob && (
                <JobApplicationModal
                    job={selectedJob}
                    mode={modalMode}
                    initialFormData={savedFormData}
                    quickApplyData={quickApplyData}
                    onClose={handleCloseModal}
                    onContinue={handleContinue}
                    onSubmitApplication={handleSubmitSuccess}
                />
            )}
        </div>
    );
};

export default ArticleshipList;
